// src/app/components/reporting/components/compose-phase.tsx
//
// Phase 2 of Insights & Reports: the report studio, the sources tray, and the
// draft it produces.
//
// Source: handoff-reporting §5 and the "Compose" screen of
// `Insights and Reports - Polished.dc.html`.

import { ArrowRight, FileText, LoaderCircle, Sparkles } from "lucide-react";
import { Button, EmptyState, Icon } from "@/app/components/kit";
import { moduleIdentity, shortLabel } from "../module-identity";
import { sentenceCase } from "../format";
import { AUDIENCES, FORMATS, QUICK_STARTS } from "../types";
import type { SavedInsight } from "../types";

/* ── audience and format ────────────────────────────────────────────────────
   Three mutually exclusive options with one selected is a radio group, whatever
   it is drawn as. Drawing it as buttons and leaving it at that is a defect this
   run has already found on two other screens: no group role, no aria-checked,
   no arrow keys. */

function ChoiceRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (v: string) => void;
}) {
  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const at = options.indexOf(value);
    let next = at;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") next = (at + 1) % options.length;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp")
      next = (at - 1 + options.length) % options.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = options.length - 1;
    else return;
    event.preventDefault();
    onChange(options[next]);
    (
      event.currentTarget.querySelector(`[data-value="${options[next]}"]`) as HTMLElement | null
    )?.focus();
  }

  return (
    <div className="upm-ir-choice">
      <span className="upm-ir-choice__label" id={`ir-choice-${label}`}>
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={`ir-choice-${label}`} onKeyDown={onKeyDown}>
        {options.map((option) => {
          const on = option === value;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              data-value={option}
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              className="upm-ir-pill"
              onClick={() => onChange(option)}
            >
              {sentenceCase(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── the phase ────────────────────────────────────────────────────────────── */

export interface ComposePhaseProps {
  selected: SavedInsight[];
  prompt: string;
  onPromptChange: (v: string) => void;
  audience: string;
  onAudienceChange: (v: string) => void;
  format: string;
  onFormatChange: (v: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  error: Error | null;
  onEditSelection: () => void;
  /** The draft, once there is one. Rendered by the page so the editor can
      replace it without this component knowing about editing at all. */
  draft?: React.ReactNode;
}

export function ComposePhase({
  selected,
  prompt,
  onPromptChange,
  audience,
  onAudienceChange,
  format,
  onFormatChange,
  onGenerate,
  isGenerating,
  error,
  onEditSelection,
  draft,
}: ComposePhaseProps) {
  const n = selected.length;
  const ready = prompt.trim().length > 0 && !isGenerating;

  return (
    <>
      <section className="upm-ir-studio" aria-label="Report studio">
        <div className="upm-ir-studio__aura" aria-hidden="true" />
        <div className="upm-ir-studio__inner">
          <div className="upm-ir-studio__head">
            <span className="upm-ir-orb">
              {/* White on violet is the reasoning accent's own on-fill colour.
                  White on the brand teal is 2.1:1 and is the contrast bug this
                  redesign removes in three places — so the orb is violet and
                  nothing on this page puts white on teal. */}
              <Icon icon={Sparkles} />
            </span>
            <div className="upm-ir-studio__headmain">
              <div className="upm-ir-eyebrow">Report studio</div>
              <h2 className="upm-ir-studio__title">What should this report say?</h2>
              <p className="upm-ir-studio__sub">
                {n > 0
                  ? `Tell me the story you need and who it is for. I will build it from the ${n} ${
                      n === 1 ? "insight" : "insights"
                    } you picked.`
                  : "Tell me the story you need and who it is for. I will build it from everything in your library."}
              </p>
            </div>
          </div>

          {/* The prompt bar. Not a kit field: it carries a persistent violet
              glow ring, a shortcut hint and a gradient send control, and none
              of those is something a text input should grow a prop for. The
              INPUT inside it is a plain input and keeps the kit's focus ring,
              which is drawn inside the glow rather than replaced by it. */}
          <div className="upm-ir-prompt">
            <input
              className="upm-ir-prompt__input"
              type="text"
              value={prompt}
              placeholder="Describe the report you need…"
              aria-label="Describe the report you need"
              onChange={(e) => onPromptChange(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && ready) onGenerate();
              }}
            />
            <span className="upm-ir-prompt__hint" aria-hidden="true">
              ⌘↵
            </span>
            <button
              type="button"
              className={
                isGenerating ? "upm-ir-prompt__send upm-ir-prompt__send--working" : "upm-ir-prompt__send"
              }
              onClick={onGenerate}
              disabled={!ready}
              aria-label="Generate this report"
            >
              <Icon icon={isGenerating ? LoaderCircle : ArrowRight} size="lg" />
            </button>
          </div>

          {/* The generating state is the orb working, not a grey modal spinner
              over a dimmed page. AI authorship is what this module IS, so the
              moment it authors something should look like the module rather
              than like a page that has stopped responding. The bar below the
              prompt streams; nothing on the page is hidden while it runs. */}
          {isGenerating ? (
            <div className="upm-ir-working" role="status" aria-live="polite">
              <span className="upm-ir-working__bar" aria-hidden="true" />
              <span className="upm-ir-working__label">
                Composing from {n > 0 ? n : "your saved"} {n === 1 ? "insight" : "insights"}
              </span>
            </div>
          ) : null}

          <div className="upm-ir-choices">
            <ChoiceRow
              label="Audience"
              options={AUDIENCES}
              value={audience}
              onChange={onAudienceChange}
            />
            <ChoiceRow label="Format" options={FORMATS} value={format} onChange={onFormatChange} />
          </div>

          <div className="upm-ir-qs-lead">
            <Icon icon={Sparkles} size="sm" className="upm-ir-qs-lead__icon" />
            Or start from a shape I know:
          </div>
          <div className="upm-ir-qs-grid">
            {QUICK_STARTS.map((q) => (
              <button
                key={q.id}
                type="button"
                className="upm-ir-qs"
                onClick={() => {
                  onPromptChange(q.prompt);
                  onFormatChange(q.format);
                  onAudienceChange(q.audience);
                }}
              >
                <span className="upm-ir-qs__format" style={{ color: q.color }}>
                  {sentenceCase(q.format)}
                </span>
                <span className="upm-ir-qs__prompt">{q.prompt}</span>
                <span className="upm-ir-qs__audience">For {sentenceCase(q.audience)}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {n > 0 ? (
        <section className="upm-ir-tray" aria-label="Sources">
          <div className="upm-ir-tray__head">
            <h3 className="upm-ir-tray__title">Sources</h3>
            <span className="upm-ir-tray__meta">
              {n} {n === 1 ? "insight" : "insights"} feeding this report
            </span>
            <span className="upm-ir-tray__edit">
              <Button variant="ghost" onClick={onEditSelection}>
                Edit selection
              </Button>
            </span>
          </div>
          <div className="upm-ir-tray__pills">
            {selected.map((i) => (
              <span
                key={i.id}
                className="upm-ir-srcpill"
                style={{ borderLeftColor: moduleIdentity(i.module).color }}
              >
                {shortLabel(i.title)}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {error ? (
        <EmptyState
          icon={FileText}
          title="That report could not be composed"
          description={error.message}
        />
      ) : null}

      {/* The draft area always says something. With no draft and nothing
          running it says what would produce one — an empty region under a
          composer reads as a page that failed to load. */}
      {draft ?? (
        isGenerating ? null : (
          <EmptyState
            icon={FileText}
            title="No draft yet"
            description={
              n > 0
                ? "Describe the report you need above, or pick one of the shapes, and the composed draft lands here."
                : "Pick some insights in Library first, then describe what you need. The composed draft lands here."
            }
          />
        )
      )}
    </>
  );
}
